import ModalEditTable from '../CRUDUtils/ModalEditTable';
import {Button, Modal, Dropdown, message, Descriptions, Alert, Select, Space, DatePicker} from 'antd';
import { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import {MoreOutlined  } from '@ant-design/icons';
import {Link} from 'react-router-dom';
import React from 'react';
import './Detections.scss';

import ActionDropdown from '../CRUDUtils/ActionDropdown';
import { apiClient } from '../../api_actions';
import { useMobileStore, useIsSuperAdmin, useIsValidator, useIsOrbitalUser } from '../../store';
import humanizeString, {capitalizeFirstLetter} from '../../utils';

export default function DetectionsPage() {

  const [actionOptions, setActionOptions] = useState([]);
  const [confirmModalVisible, setConfirmModalVisible] = useState(false);
  const [confirmMediaModalVisible, setConfirmMediaModalVisible] = useState(false)
  const [modalData, setModalData] = useState({ action: null, value: null, record: null });
  const [loading, setLoading] = useState(false)
  const [modalTimestamp, setModalTimestamp] = useState(Date.now())
  const [videoUrl, setVideoUrl] = useState(null);
  const isMobile = useMobileStore((state) => state.isMobile);
  const [detectionTypes, setDetectionTypes] = useState([]);
  const [selectedDetectionType, setSelectedDetectionType] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedValidation, setSelectedValidation] = useState(null);
  const [selectedValidationId, setSelectedValidationId] = useState(null)
  const isSuperAdmin = useIsSuperAdmin()
  const isValidator = useIsValidator()
  const isOrbitalUser = useIsOrbitalUser()
  const MINUTE_MS = 60000
  const [searchInput, setSearchInput] = useState(dayjs().subtract(30, 'day').startOf('day').unix().toString());
  const [secondarySearchInput, setSecondarySearchInput] = useState(dayjs().endOf('day').unix().toString())
  const { RangePicker } = DatePicker;
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [reloadTimestamp, setReloadTimestamp] = useState(Date.now())

  useEffect(() => {
    loadActions();
    loadDetectionTypes();
    loadLocations();
    if (isOrbitalUser) {
      loadCustomers();
    }
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setReloadTimestamp(Date.now());
    }, 30_000);
  }, []);

  const loadActions = async () => {
    try{
    const result = await apiClient.post('/read/validationtypes');
    const items = result.data.map(action => ({
      key: action.validation_type_id,
      label: action.validation_type,  // or whatever the unique field is
      value: action.validation_type,  // or whatever the display field is
    }));
    setActionOptions(items);
  } catch (error) {
    console.error('Failed to load validation types:', error);
  }
  };

  // Function to load detection types
  const loadDetectionTypes = async () => {
    try {
      const result = await apiClient.post('read/detectiontypes');
      const detectionTypeOptions = result.data.map(detectionType => ({
        label: detectionType.detection_type_desc,
        value: detectionType.detection_type_desc,
      }));
      setDetectionTypes(detectionTypeOptions);
    } catch (error) {
      console.error('Failed to load detection types:', error);
    }
  };

  // Function to load locations
  const loadLocations = async () => {
    try {
      const result = await apiClient.post('read/locations');
      const locationOptions = result.data
        .map(location => ({
          label: location.name,
          value: `${location.name} - ${location.location_id}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label));
      setLocations(locationOptions);
    } catch (error) {
      console.error('Failed to load locations:', error);
    }
  };

  const handleActionClick = (record, option) => {
    
    setModalData({ action: option.key, value: option.label, record: record });
    setConfirmModalVisible(true);
  };

  const loadCustomers = async () => {
    try {
      const result = await apiClient.post('read/customers');
      const customerOptions = result.data.map(customer => ({
        label: customer.name,
        value: customer.customer_id,
      }));
      setCustomers(customerOptions);
    } catch (error) {
      console.error('Failed to load customers:', error);
    }
  };


  const handleConfirm = async () => {
    let postdata = {...modalData.record}
    postdata.data_validation_type.validation_type_id = Number(modalData.action)

    await apiClient.post('/update/detection', postdata);
    
    setConfirmModalVisible(false);
    setModalData({ action: null, value: null, record: null });
    setModalTimestamp(Date.now())
  };

  const columns = [

    { 
      title: isMobile ? 'Time' : 'Timestamp', 
      dataIndex: 'unix_timestamp', 
      key: 'unix_timestamp',
      width: 120,
      render: (timestamp) => dayjs(timestamp).local().format('MM/DD/YY HH:mm'),
    },
    {
      title: "Detection",
      key: "url",
      ellipsis: true,
      width: 60,
      render: (_, record) => {
        return (
          <Button
            type="link"
            onClick={async (e) => {
              e.stopPropagation();

              try {
                // Optional: show a loading indicator
                setLoading(true);
                setConfirmMediaModalVisible(true);
                const res = await apiClient.get(`/read/media/${record.url}`, { responseType: 'blob' });
                const videoUrl = URL.createObjectURL(res);
                setVideoUrl(videoUrl);

                
              } catch (err) {
                message.error("Could not load video");
              } finally {
                setLoading(false);
              }
            }}
          >
            {`${record.detection_type.detection_type_desc}`}
          </Button>
        );
      },
    },
    { 
      title: 'Trailer', 
      dataIndex: ['inference_hardware_info','tag'], 
      key: ['inference_hardware_info','tag'],
      ellipsis: true,
      width: 60,
      hideOnMobile: true,
    },
    { 
      title: 'Camera', 
      dataIndex: ['hardware_info','hardware_type','hardware_meta'], 
      key: ['hardware_info','hardware_type','hardware_meta'],
      hideOnMobile: true,
      render: (text) => capitalizeFirstLetter(humanizeString(text)) || 'N/A'
    },
    { 
      title: 'Location', 
      dataIndex: ['location','name'], 
      key: ['location','name'],
      ellipsis: true,
      width: 60,
    },
    { 
      title: 'VMS', 
      dataIndex: 'deployment_id', 
      key: 'deployment_id',
      ellipsis: true,
      width: 60,
      render: (_, record) => (
        <div>
          <Link to={`/vms/${record.deployment_id}/${record.location.name}/${record.unix_timestamp}`}>VMS</Link>
        </div>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 60,
      isHidden: (!isSuperAdmin && !isValidator),
      hideOnMobile: true,
      render: (_, record) => (
        <ActionDropdown
          record={record}
          actionOptions={actionOptions}
          handleActionClick={handleActionClick}
        />
      ),
    },
    { 
      title: 'Validation', 
      ellipsis: true,
      width: 60,
      dataIndex: ['data_validation_type','validation_type'], 
      key: ['data_validation_type','validation_type'],
      isHidden: !isSuperAdmin,
    },
    { 
      title: 'Sent', 
      dataIndex: ['sent_flag'], 
      key: ['sent_flag'],
      ellipsis: true,
      width: 60,
      hideOnMobile:true,
      isHidden: (!isSuperAdmin && !isValidator),
    },
    // { 
    //   title: 'Validator', 
    //   dataIndex: ['validator_user_email'], 
    //   key: ['validator_user_email'],
    //   ellipsis: true,
    //   width: 60,
    //   hideOnMobile:true,
    //   isHidden: (!isSuperAdmin && !isValidator),
    // }
  ].filter(col => col != null && !(isMobile && col.hideOnMobile) && !col.isHidden);

  return (
    <div className='container-wrapper'>
      <div>
        <h2>Events</h2>
      </div>
      <div className="detections-filter-wrapper">
        <div className="detections-filter-controls">
          {(<label className="detections-filter-label">Filter by:</label>)}
          {React.createElement(
              isMobile ? React.Fragment : Space.Compact,
              {},
              ...[
                  <Select
                      placeholder="Detection Type"
                      allowClear
                      value={selectedDetectionType}
                      onChange={(value) => setSelectedDetectionType(value)}
                      options={detectionTypes}
                  />,
                  <Select
                      showSearch
                      placeholder="Location"
                      allowClear
                      value={selectedLocation}
                      onChange={(label) => {
                          const locationId = label?.split(' - ').pop();
                          setSelectedLocationId(locationId);
                          setSelectedLocation(label);
                      }}
                      options={locations}
                  />,
                  isSuperAdmin && (
                    <Select
                        showSearch
                        placeholder="Validation Type"
                        allowClear
                        value={selectedValidation}
                        onChange={(label) => {
                          setSelectedValidation(label)
                        }}
                        options={actionOptions}
                    />
                  ),
                  isOrbitalUser && (
                      <Select
                          placeholder="Customer"
                          allowClear
                          value={selectedCustomer}
                          onChange={(value) => setSelectedCustomer(value)}
                          options={customers}
                      />
                  ),
                  <RangePicker
                      onChange={(value, timeString) => {
                          setSearchInput(dayjs(timeString[0]).startOf('day').unix().toString());
                          setSecondarySearchInput(dayjs(timeString[1]).endOf('day').unix().toString());
                          setModalTimestamp(Date.now());
                      }}
                      disabledDate={(current) => current && current < dayjs().subtract(1, 'years').startOf('day')}
                      format={'MM/DD/YYYY'}
                      className='rangeStyle'
                      defaultValue={[dayjs().subtract(30, 'day').startOf('day'), dayjs().endOf('day')]}
                  />
              ].filter(Boolean)
          )}
        </div>
      </div>

    <ModalEditTable
      createEndpoint=""
      readEndpoint="read/detections"
      editEndpoint=""
      deleteEndpoint=""
      continuousRefresh={false}
      refreshToken={modalTimestamp}
      selectedDetectionType={selectedDetectionType}
      selectedLocation={selectedLocationId}
      selectedCustomer={selectedCustomer}
      selectedValidation={selectedValidation}
      searchQuery="start"
      searchInput={searchInput}
      secondarySearchQuery="end"
      secondarySearchInput={secondarySearchInput}
      columns={columns}
      formFields={[]}
      canCreate={false}
      canEdit = {false}
      canView = {false}
      canDelete={false}
      infinatePages = {true}
      reloadTimestamp = {reloadTimestamp}
    />

    <Modal
      title="Confirm Event Label"
      open={confirmModalVisible}
      onOk={handleConfirm}
      onCancel={() => {
        setConfirmModalVisible(false);
        setModalData({ action: null, value: null, record: null });
      }}
    >
    <Descriptions column={1} bordered size="small" style={{ marginBottom: 16 }}>
      <Descriptions.Item label="Detection Type">
        {modalData.record?.detection_type?.detection_type_desc}
      </Descriptions.Item>
      <Descriptions.Item label="Validation">
        {modalData.value}
      </Descriptions.Item>
    </Descriptions>
    
    {modalData.value === "true positive" &&
      <Alert
        message="This will send SMS/email notifications to all users registered for this deployment."
        type="warning"
        showIcon
      />
    }
    </Modal>

    <Modal
        title="Video Preview"
        open={confirmMediaModalVisible}
        onCancel={() => setConfirmMediaModalVisible(false)}
        footer={null}
        loading={loading}
        width={1280}
      >
        {videoUrl && (
          <video
            key={videoUrl} // forces reload when switching videos
            width="100%"
            height="auto"
            controls
            style={{ borderRadius: 4 }}
            auto
          >
            <source src={videoUrl} type="video/mp4" />
            Your browser does not support HTML5 video.
          </video>
        )}
      </Modal>

    </div>
    
  );
}